import React, { useState } from 'react'
import { Card, CardContent, CardHeader, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { HorizontalStackedBarChart } from '../../../charts/BarChart'
import { formatNumber } from '../../../../helpers/UtilityHelper'
import getColor from '../../../charts/colors'
import _ from 'lodash'

const CancellationRemarksByAttempt = ({ rawData = [] }) => {
    const [view, setView] = useState('table')

    const processedData = React.useMemo(() => {
        if (!rawData || rawData.length === 0) return null

        const shipmentsWithRemarks = rawData.filter(item =>
            item.failedDeliveryRemarks && item.failedDeliveryRemarks.length > 0
        )

        if (shipmentsWithRemarks.length === 0) return null

        const remarksByAttempt = {}
        const allReasons = new Set()

        shipmentsWithRemarks.forEach(item => {
            const remarks = item.failedDeliveryRemarks || []
            remarks.forEach((remark, idx) => {
                const attemptNum = Math.min(idx + 1, 5)
                const attemptKey = attemptNum === 5 ? '5th+' : `${attemptNum}${attemptNum === 1 ? 'st' : attemptNum === 2 ? 'nd' : attemptNum === 3 ? 'rd' : 'th'}`
                const reason = (!remark?.remark && !remark?.reason) ? 'Other' : (remark.remark || remark.reason || 'Other')
                allReasons.add(reason)
                if (!remarksByAttempt[reason]) remarksByAttempt[reason] = {}
                remarksByAttempt[reason][attemptKey] = (remarksByAttempt[reason][attemptKey] || 0) + 1
            })
        })

        const attempts = ['1st', '2nd', '3rd', '4th', '5th+']
        const data = Array.from(allReasons).map(reason => {
            const row = { reason }
            let total = 0
            attempts.forEach(attempt => {
                row[attempt] = remarksByAttempt[reason]?.[attempt] || 0
                total += row[attempt]
            })
            row.total = total
            return row
        }).sort((a, b) => b.total - a.total).slice(0, 15)

        const grandTotals = { reason: 'Grand Total' }
        let grandTotal = 0
        attempts.forEach(attempt => {
            grandTotals[attempt] = data.reduce((sum, r) => sum + (r[attempt] || 0), 0)
            grandTotal += grandTotals[attempt]
        })
        grandTotals.total = grandTotal

        return { attempts, data, grandTotals }
    }, [rawData])

    if (!processedData || processedData.data.length === 0) return null

    const { attempts, data: tableData, grandTotals } = processedData

    const chartOptions = {
        yaxis: 'reason',
        data: attempts.map((attempt, i) => ({
            dataKey: attempt,
            label: `${attempt} Attempt`,
            fill: getColor(i),
        })),
        stackId: 'a',
        yLabel: 'Reason',
        showLabels: true,
        legendAlign: 'left',
    }

    return (
        <Card elevation={1}>
            <CardHeader
                title="Cancellation Remarks by Attempt"
                subheader="Distribution of delivery failure reasons by attempt number for RVP shipments"
                action={
                    <ToggleButtonGroup
                        value={view}
                        exclusive
                        size="small"
                        onChange={(_e, v) => v && setView(v)}
                    >
                        <ToggleButton value="chart">Chart</ToggleButton>
                        <ToggleButton value="table">Table</ToggleButton>
                    </ToggleButtonGroup>
                }
            />
            <CardContent>
                {view === 'chart' ? (
                    <HorizontalStackedBarChart data={tableData} chartOptions={chartOptions} height={Math.max(250, tableData.length * 35 + 80)} />
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#FFF9C4' }}>Reason</TableCell>
                                    {attempts.map(attempt => (
                                        <TableCell key={attempt} align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#FFF9C4' }}>
                                            {attempt}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', backgroundColor: '#E0E0E0' }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.map((row) => (
                                    <TableRow key={row.reason}>
                                        <TableCell sx={{ fontSize: '0.75rem' }}>{row.reason}</TableCell>
                                        {attempts.map(attempt => (
                                            <TableCell key={attempt} align="center" sx={{ fontSize: '0.75rem' }}>
                                                {formatNumber(row[attempt])}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                            {formatNumber(row.total)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ backgroundColor: '#333' }}>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem' }}>Grand Total</TableCell>
                                    {attempts.map(attempt => (
                                        <TableCell key={attempt} align="center" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            {formatNumber(grandTotals[attempt])}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {formatNumber(grandTotals.total)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    )
}

export default CancellationRemarksByAttempt
